import { QueryClient } from '@tanstack/react-query';

// Query key constants
const QUERY_KEYS = {
  ENTITY: 'entity',
  ENTITIES: 'entities',
  BY_DATABASE: 'by-database',
  BY_GROUP: 'by-group',
  BY_ID: 'by-id',
  SUPPLIERS: 'suppliers',
  PRODUCTS: 'products',
  BY_PRODUCT: 'by-product',
  BY_SUPPLIER: 'by-supplier',
} as const;

// The one key factory for entity queries. Kept free of other imports so any
// hooks module can invalidate entity lists without an import cycle.
export const entityKeys = {
  all: () => [QUERY_KEYS.ENTITY] as const,
  /** Prefix key for every entity list: opened Database lists and team lists. */
  entities: () => [...entityKeys.all(), QUERY_KEYS.ENTITIES] as const,
  entitiesByDatabase: (
    databaseId: string,
    options?: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      search?: string;
      tab?:
        | 'All'
        | 'New'
        | 'Not Reviewed'
        | 'Review Queue'
        | 'Quarantined'
        | 'Barred'
        | 'Cleared'
        | string;
      statusFilters?: string[];
      riskFilters?: string[];
      hasNewResults?: 'yes' | 'no' | null;
      impactedByError?: 'yes' | 'no';
      statusReset?: boolean;
      previousStatus?: string;
      lensFilters?: string[];
      hitCountRanges?: string[];
      nameStartsWith?: string[];
    },
  ) =>
    [
      ...entityKeys.entities(),
      QUERY_KEYS.BY_DATABASE,
      databaseId,
      {
        page: options?.page || 1,
        limit: options?.limit || 10,
        sortBy: options?.sortBy || 'hit_count',
        sortOrder: options?.sortOrder || 'desc',
        search: options?.search || '',
        tab: options?.tab || 'All',
        statusFilters: options?.statusFilters || [],
        riskFilters: options?.riskFilters || [],
        hasNewResults: options?.hasNewResults || null,
        impactedByError: options?.impactedByError,
        statusReset: options?.statusReset || false,
        previousStatus: options?.previousStatus || '',
        lensFilters: options?.lensFilters || [],
        hitCountRanges: options?.hitCountRanges || [],
        nameStartsWith: options?.nameStartsWith || [],
      },
    ] as const,
  /** Prefix key for invalidating every page/filter of a database entity list. */
  entitiesByDatabasePrefix: (databaseId: string) =>
    [...entityKeys.entities(), QUERY_KEYS.BY_DATABASE, databaseId] as const,
  /** Prefix key for every team's entity lists (Entities Overview, Review Queue, roll-ups). */
  entitiesByGroupAll: () =>
    [...entityKeys.entities(), QUERY_KEYS.BY_GROUP] as const,
  /** Prefix key for invalidating every page/filter of a team entity list. */
  entitiesByGroupPrefix: (groupId: string) =>
    [...entityKeys.entitiesByGroupAll(), groupId] as const,

  // Updated entitiesByGroup to support pagination parameters
  entitiesByGroup: (
    groupId: string,
    options?: {
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      search?: string;
      databaseName?: string;
      exlusions?: string[];
      statusFilters?: string[];
      riskFilters?: string[];
      hasNewResults?: 'yes' | 'no' | null;
      statusReset?: boolean;
      previousStatus?: string;
      lensFilters?: string[];
      hitCountRanges?: string[];
      nameStartsWith?: string[];
      entitySearchTerms?: string[];
      createdSince?: string;
    },
  ) =>
    [
      ...entityKeys.entitiesByGroupPrefix(groupId),
      {
        page: options?.page || 1,
        limit: options?.limit || 10,
        sortBy: options?.sortBy || 'hit_count',
        sortOrder: options?.sortOrder || 'desc',
        search: options?.search || '',
        databaseName: options?.databaseName || '',
        exlusions: options?.exlusions || [],
        statusFilters: options?.statusFilters || [],
        riskFilters: options?.riskFilters || [],
        hasNewResults: options?.hasNewResults || null,
        statusReset: options?.statusReset || false,
        previousStatus: options?.previousStatus || '',
        lensFilters: options?.lensFilters || [],
        hitCountRanges: options?.hitCountRanges || [],
        nameStartsWith: options?.nameStartsWith || [],
        entitySearchTerms: options?.entitySearchTerms || [],
        createdSince: options?.createdSince || '',
      },
    ] as const,

  entity: () => [...entityKeys.all(), QUERY_KEYS.BY_ID] as const,
  entityById: (id: string, databaseId?: string, selectedTeamId?: string) =>
    [
      ...entityKeys.entity(),
      id,
      ...(databaseId || selectedTeamId
        ? [{ databaseId: databaseId, selectedTeamId: selectedTeamId }]
        : []),
    ] as const,
  entityHierarchy: (
    id: string,
    direction: 'above' | 'below' | 'both',
    maxDepth: number,
  ) => [...entityKeys.entity(), 'hierarchy', id, direction, maxDepth] as const,
  /** Prefix key for the children of every expanded row. */
  entityChildrenAll: () => ['entity-children'] as const,
  entityChildren: (
    entityId: string | undefined,
    databaseId: string | undefined,
    selectedTeamId: string | undefined,
  ) =>
    [...entityKeys.entityChildrenAll(), entityId, databaseId, selectedTeamId] as const,
  ownershipMatches: (id: string) =>
    [...entityKeys.entity(), id, 'ownership-matches'] as const,
  ownershipMatchesPage: (id: string, page: number, size: number) =>
    [...entityKeys.ownershipMatches(id), page, size] as const,
  suppliers: () => [...entityKeys.all(), QUERY_KEYS.SUPPLIERS] as const,
  suppliersByProduct: (productId: string) =>
    [...entityKeys.suppliers(), QUERY_KEYS.BY_PRODUCT, productId] as const,
  products: () => [...entityKeys.all(), QUERY_KEYS.PRODUCTS] as const,
  productsBySupplier: (supplierId: string) =>
    [...entityKeys.products(), QUERY_KEYS.BY_SUPPLIER, supplierId] as const,
};

/**
 * Marks entity lists stale and refetches the ones on screen, each once.
 *
 * - With a databaseId: that Database's lists, plus every team list (they show
 *   its rows too, and would otherwise be served from cache for the stale time).
 * - Without: every entity list.
 *
 * The two prefixes never overlap, so no active list is fetched twice. Call it
 * once per settled action; a second call in the same tick cancels the first
 * fetch and sends another request.
 */
export const invalidateEntityLists = (
  queryClient: QueryClient,
  databaseId?: string,
): Promise<unknown> => {
  if (!databaseId) {
    return queryClient.invalidateQueries({ queryKey: entityKeys.entities() });
  }
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: entityKeys.entitiesByDatabasePrefix(databaseId),
    }),
    queryClient.invalidateQueries({
      queryKey: entityKeys.entitiesByGroupAll(),
    }),
  ]);
};
